import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../lib/utils';

/** One status palette for badges and domain status adapters. */
export const badgeToneClasses = {
  success: 'bg-status-success-bg text-status-success-fg border-transparent',
  danger: 'bg-status-danger-bg text-status-danger-fg border-transparent',
  critical: 'bg-status-critical-bg text-status-critical-fg border-transparent',
  warning: 'bg-status-warning-bg text-status-warning-fg border-transparent',
  information: 'bg-status-information-bg text-status-information-fg border-transparent',
  neutral: 'bg-status-neutral-bg text-status-neutral-fg border-transparent',
} as const;

export const badgeDotClasses = {
  success: 'bg-status-success-fill',
  danger: 'bg-status-danger-fill',
  critical: 'bg-status-critical-fill',
  warning: 'bg-status-warning-fill',
  information: 'bg-status-information-fill',
  neutral: 'bg-status-neutral-fill',
} as const;

const badgeTones = {
  compliant: 'success',
  'non-compliant': 'danger',
  warning: 'warning',
  pending: 'warning',
  draft: 'neutral',
  success: 'success',
  error: 'danger',
  info: 'information',
  critical: 'critical',
  danger: 'danger',
  information: 'information',
  neutral: 'neutral',
  approval: 'information',
} as const;

const badgeVariants = cva(
  'inline-flex w-auto self-start justify-self-start max-w-full shrink-0 whitespace-nowrap items-center gap-1.5 rounded-badge border px-2 py-0.5 font-body text-caption font-medium tracking-normal',
  {
    variants: {
      variant: {
        compliant: badgeToneClasses.success,
        'non-compliant': badgeToneClasses.danger,
        warning: badgeToneClasses.warning,
        pending: badgeToneClasses.warning,
        draft: badgeToneClasses.neutral,
        success: badgeToneClasses.success,
        error: badgeToneClasses.danger,
        info: badgeToneClasses.information,
        critical: badgeToneClasses.critical,
        danger: badgeToneClasses.danger,
        information: badgeToneClasses.information,
        neutral: badgeToneClasses.neutral,
        approval: badgeToneClasses.information,
      },
    },
    defaultVariants: { variant: 'draft' },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>, VariantProps<typeof badgeVariants> {
  dot?: boolean;
}

function Badge({ className, variant, children, title, dot = true, ...props }: BadgeProps) {
  const tone = badgeTones[variant ?? 'draft'];
  return (
    <span
      className={cn(badgeVariants({ variant }), className)}
      title={title ?? (typeof children === 'string' ? children : undefined)}
      {...props}
      data-ui="badge"
      data-status-tone={tone}
      data-experience-tone={tone}
    >
      {dot && (
        <span
          className={cn('h-1.5 w-1.5 shrink-0 rounded-full', badgeDotClasses[tone])}
          aria-hidden="true"
        />
      )}
      {typeof children === 'string' || typeof children === 'number' ? (
        <span className="min-w-0 truncate">{children}</span>
      ) : (
        children
      )}
    </span>
  );
}

export { Badge, badgeVariants };
